import React, { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ToolbarTooltipButton from "../../../components/common/ToolbarTooltipButton";
import Button from "../../../components/common/Button";
import useThemes, { refreshThemes } from "../../../utils/useThemes";
import useUserPlan from "../../../utils/useUserPlan";
import { createTheme } from "../../../utils/brandApi";
import { useCreateFlow } from "../../../flow/CreateFlowContext";
import { CAROUSEL_DEFAULT_DESIGN } from "../../../flow/carouselDefaults";
import { CANVAS_W, CANVAS_H } from "../../../carousel/canvas";
import {
  PALETTES,
  CUSTOM_PALETTE_ID,
  MIN_TEXT_CONTRAST,
  contrastRatio,
  normalizeHex,
  resolvePalette,
} from "../../../carousel/palettes";
import {
  FONT_PAIRS,
  SIZE_RANGES,
  resolveFontPair,
  loadCarouselFonts,
} from "../../../carousel/fonts";
import {
  drawSlide,
  LAYOUT_IDS,
  LINE_WEIGHT_RANGE,
} from "../../../carousel/drawSlide";
import { BG_STYLE_IDS } from "../../../carousel/backgrounds";
import CarouselPanel from "./CarouselPanel";
import CarouselThumb from "./CarouselThumb";
import logoIcon from "../../../assets/tips/uploadLogo.svg";
import arrowLeft from "../../../assets/common/arrow-left.svg";
import arrowRight from "../../../assets/common/arrow-right.svg";
import eye from "../../../assets/posts/eye.svg";
import carouselIcon from "../../../assets/tips/carousel.svg";
import regenerateIcon from "../../../assets/tips/regenerate.svg";
import fontSettingsIcon from "../../../assets/tips/font-settings.svg";
import layoutIcon from "../../../assets/tips/layout.svg";
import colorsIcon from "../../../assets/tips/colors.svg";
import backgroundIcon from "../../../assets/tips/background.svg";

// Splits one point into headline + body. Prefers an explicit separator,
// then a short opening sentence. Falls back to no headline rather than
// repeating the body's first few words.
const splitPoint = (text = "") => {
  const s = String(text).trim();
  const sep = s.match(/^(.{8,60}?)\s*[:—–]\s+(.+)$/s);
  if (sep) return { title: sep[1].trim(), body: sep[2].trim() };
  const sentence = s.match(/^(.{8,60}?[.!?])\s+(.+)$/s);
  if (sentence) return { title: sentence[1].replace(/[.]$/, "").trim(), body: sentence[2].trim() };
  if (s.length <= 120) return { title: s, body: "" };
  return { title: "", body: s };
};

// ─── Heuristic plan (exported for use in Home.jsx) ───────────────────────────
export function heuristicPlan(post) {
  // No body → no carousel. The media step tells the user why.
  if (!post?.cuerpo?.trim()) return [];
  const rawLines = post.cuerpo.split("\n").map((l) => l.trim()).filter(Boolean);
  const bullets = rawLines.filter((l) => /^[•\-\*]/.test(l));

  let contentSlides;
  if (bullets.length >= 3) {
    contentSlides = bullets.slice(0, 6).map((b) => ({
      type: "content",
      ...splitPoint(b.replace(/^[•\-\*]\s*/, "")),
    }));
  } else {
    const paragraphs = post.cuerpo.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
    contentSlides = paragraphs.slice(0, 6).map((p) => ({ type: "content", ...splitPoint(p) }));
  }

  // The closing slide only when there's something to put on it
  const hasOutro = !!post.cta?.trim() || (post.hashtags || []).length > 0;

  return [
    { type: "hook", title: post.hook || "", subtitle: "" },
    ...contentSlides,
    ...(hasOutro
      ? [{ type: "outro", title: post.cta || "", hashtags: post.hashtags || [] }]
      : []),
  ];
}

// ─── CarouselSection ──────────────────────────────────────────────────────────
// Own background image: checked in the browser before it is used
const BG_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const BG_IMAGE_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

// Font settings shows only the sizes used on the slide on screen.
// The hook subtitle uses the Body size.
const SIZE_KEYS_BY_SLIDE = {
  hook: ["h1"],
  content: ["h2", "body"],
  outro: ["cta", "hashtags"],
};

// English fallbacks until the translations are added (stage 7)
const SIZE_LABELS = {
  h1: "H1 — Hook title",
  h2: "H2 — Slide title",
  cta: "CTA — Last slide title",
  body: "Body text",
  hashtags: "Hashtags",
};

export default function CarouselSection({
  slides = [],
  onSlidesChange,
  mobileViewStep,
  onLogoUploadRequest,
  onExportReady,
  onPreviewClick,
  onDownload,
  logoBitmap = null,
  logoRel = null,
  onLogoChange,
  onLogoRemove,
  userEmail = "",
  onOpenAccount,
  onPreviewStateChange,
}) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [exporting, setExporting] = useState(false);
  // The whole look of the carousel (layout, palette, fonts, sizes,
  // background) lives in the shared flow state, so it survives going Back
  // and the mobile screens being rebuilt.
  const { carouselDesign, setCarouselDesign } = useCreateFlow();
  const design = {
    ...CAROUSEL_DEFAULT_DESIGN,
    ...carouselDesign,
    sizes: { ...CAROUSEL_DEFAULT_DESIGN.sizes, ...carouselDesign?.sizes },
    bgOptions: {
      ...CAROUSEL_DEFAULT_DESIGN.bgOptions, ...carouselDesign?.bgOptions },
  };
  const updateDesign = (patch) =>
    setCarouselDesign((prev) => ({ ...prev, ...patch }));
  const setSize = (key, value) =>
    updateDesign({ sizes: { ...design.sizes, [key]: value } });
  const [fontsReady, setFontsReady] = useState(false);
  const [openPanel, setOpenPanel] = useState(null);
  const designSnapshot = useRef(null);
  const showFontTools = openPanel === "font";
  const showLogoTools = openPanel === "logo";
  const isFirstLogoRender = useRef(true);
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const { themes } = useThemes(userEmail);

  // Everything drawSlide needs except the slide itself. Also sent to the
  // preview window, so it always matches the editor.
  // design.paletteId is a built-in key or a brand theme_id.
  const drawOptions = () => ({
    palette: resolvePalette(design.paletteId, themes, design.customPalette),
    fonts: resolveFontPair(design.fontPairId),
    sizes: design.sizes,
    lineWeight: design.lineWeight,
    layoutId: design.layoutId,
    bgStyle: design.bgStyle,
    bgOptions: design.bgOptions,
    bgOverlay: design.bgOverlay,
    bgBitmap: design.bgBitmap,
    logoBitmap,
    logoRel,
  });

  // Load the carousel fonts once, then redraw with them
  useEffect(() => {
    let alive = true;
    loadCarouselFonts().then(() => {
      if (alive) setFontsReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !slides[currentIndex]) return;
    const ctx = canvas.getContext("2d");
    drawSlide(ctx, {
      ...drawOptions(),
      slide: slides[currentIndex],
      index: currentIndex,
      total: slides.length,
      showLogoHandle: showLogoTools,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slides,
    currentIndex,
    logoBitmap,
    logoRel,
    carouselDesign,
    themes,
    showLogoTools,
    fontsReady,
  ]);

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  const closePanel = () => setOpenPanel(null);

  // While a panel is open, the other tools are disabled and the open one
  // does nothing: the person leaves the panel with Cancel or Apply, so
  // their changes are never dropped by accident.
  const panelBusy = openPanel !== null;

  const togglePanel = (id) => {
    if (panelBusy) return;
    designSnapshot.current = carouselDesign;
    setOpenPanel(id);
  };

  // Back to the look from when the panel opened
  const cancelPanel = () => {
    if (designSnapshot.current) setCarouselDesign(designSnapshot.current);
    closePanel();
  };

  const resetFontTools = () => {
    updateDesign({
      fontPairId: CAROUSEL_DEFAULT_DESIGN.fontPairId,
      sizes: CAROUSEL_DEFAULT_DESIGN.sizes,
      lineWeight: CAROUSEL_DEFAULT_DESIGN.lineWeight,
    });
  };

  const resetColors = () => {
    updateDesign({ paletteId: CAROUSEL_DEFAULT_DESIGN.paletteId });
  };

  // Custom colors: 3 pickers, a contrast check and "Save as brand colors",
  // which creates a brand theme exactly like the account modal does
  // (same API, same quota).
  const userPlan = useUserPlan();
  const themeQuota = userPlan === "premium" ? 5 : 2;
  const [customName, setCustomName] = useState("");
  const [customError, setCustomError] = useState("");
  const [customSaved, setCustomSaved] = useState(false);
  const [savingCustom, setSavingCustom] = useState(false);
  const isCustom = design.paletteId === CUSTOM_PALETTE_ID;
  const currentPalette = resolvePalette(
    design.paletteId,
    themes,
    design.customPalette
  );
  const textContrast = contrastRatio(currentPalette.text, currentPalette.bg);

  // Starts from the palette on screen, so it is easy to tweak
  const startCustom = () => {
    updateDesign({
      paletteId: CUSTOM_PALETTE_ID,
      customPalette: {
        name: "Custom",
        bg: currentPalette.bg,
        text: currentPalette.text,
        accent: currentPalette.accent,
      },
    });
    setCustomSaved(false);
    setCustomError("");
  };

  const setCustomColor = (key, value) => {
    updateDesign({
      customPalette: { ...currentPalette, name: "Custom", [key]: value },
    });
    setCustomSaved(false);
  };

  const saveCustomAsBrand = async () => {
    if (savingCustom) return;
    if (!customName.trim()) {
      setCustomError(t("userAccountModal.themes.nameRequired"));
      return;
    }
    setCustomError("");
    setSavingCustom(true);
    try {
      const created = await createTheme(userEmail, userPlan, {
        name: customName.trim(),
        background: currentPalette.bg,
        text: currentPalette.text,
        highlight: currentPalette.accent,
      });
      await refreshThemes(userEmail);
      // Switch to the new brand theme when the API tells us its id
      if (created?.theme_id) updateDesign({ paletteId: created.theme_id });
      setCustomName("");
      setCustomSaved(true);
    } catch (err) {
      setCustomError(err.message || t("userAccountModal.themes.saveError"));
    } finally {
      setSavingCustom(false);
    }
  };

  const resetLayout = () => {
    updateDesign({ layoutId: CAROUSEL_DEFAULT_DESIGN.layoutId });
  };

  // Background: pattern settings live in design.bgOptions
  const [showBgAdvanced, setShowBgAdvanced] = useState(false);
  const setBgOption = (key, value) =>
    updateDesign({ bgOptions: { ...design.bgOptions, [key]: value } });

  const resetBackground = () => {
    updateDesign({
      bgStyle: CAROUSEL_DEFAULT_DESIGN.bgStyle,
      bgOptions: CAROUSEL_DEFAULT_DESIGN.bgOptions,
      bgOverlay: CAROUSEL_DEFAULT_DESIGN.bgOverlay,
      bgBitmap: null,
    });
    setBgError("");
  };

  // Own background image. It is kept in the design state, so Cancel and
  // Reset handle it like the other settings. It is not saved with the draft:
  // only the finished PDF is.
  const bgInputRef = useRef(null);
  const [bgError, setBgError] = useState("");

  const handleBgUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // the same file can be picked again
    if (!file) return;
    setBgError("");
    if (!BG_IMAGE_TYPES.includes(file.type)) {
      setBgError(t("carousel.bg.errorType"));
      return;
    }
    if (file.size > BG_IMAGE_MAX_BYTES) {
      setBgError(t("carousel.bg.errorSize"));
      return;
    }
    try {
      const bitmap = await createImageBitmap(file);
      updateDesign({ bgBitmap: bitmap });
    } catch (err) {
      console.error("Background image could not be read:", err);
      setBgError(t("carousel.bg.errorRead"));
    }
  };

  const dragRef = useRef(null);

  const logoRectPx = () => {
    if (!logoBitmap || !logoRel) return null;
    const w = logoRel.wPct * CANVAS_W;
    const h = w / (logoBitmap.width / logoBitmap.height);
    return { x: logoRel.xPct * CANVAS_W, y: logoRel.yPct * CANVAS_H, w, h };
  };

  const toCanvasCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((e.clientY - rect.top) / rect.height) * CANVAS_H,
    };
  };

  const onPointerDown = (e) => {
    if (!showLogoTools || !logoBitmap || !logoRel) return;
    const r = logoRectPx();
    const { x: cx, y: cy } = toCanvasCoords(e);
    const hx = r.x + r.w;
    const hy = r.y + r.h;
    // Generous radius: the canvas is displayed at roughly a third of its size
    const nearHandle = Math.hypot(cx - hx, cy - hy) <= 45;
    const inBody = cx >= r.x && cx <= hx && cy >= r.y && cy <= hy;
    if (!nearHandle && !inBody) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    dragRef.current = {
      mode: nearHandle ? "resize" : "move",
      startX: cx, startY: cy,
      origX: r.x, origY: r.y, origW: r.w,
    };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const r = logoRectPx();
    const { x: cx, y: cy } = toCanvasCoords(e);
    if (d.mode === "move") {
      const nx = clamp(d.origX + (cx - d.startX), 0, CANVAS_W - r.w);
      const ny = clamp(d.origY + (cy - d.startY), 0, CANVAS_H - r.h);
      onLogoChange?.({
        ...logoRel,
        xPct: nx / CANVAS_W,
        yPct: ny / CANVAS_H,
      });
    } else {
      const w = clamp(
        d.origW + (cx - d.startX),
        CANVAS_W * 0.05,
        CANVAS_W * 0.4
      );
      onLogoChange?.({ ...logoRel, wPct: w / CANVAS_W });
    }
  };

  const onPointerUp = (e) => {
    dragRef.current = null;
    canvasRef.current?.releasePointerCapture?.(e.pointerId);
  };

  // Open the tools as soon as a logo lands, but not on mount.
  useEffect(() => {
    if (isFirstLogoRender.current) {
      isFirstLogoRender.current = false;
      return;
    }
    if (logoBitmap) setOpenPanel("logo");
  }, [logoBitmap]);

  useEffect(() => { draw(); }, [draw]);
  // On a phone the panel opens as a sheet over the bottom of the screen:
  // bring the slide to the top so the result stays visible while adjusting
  useEffect(() => {
    if (!openPanel || !canvasRef.current) return;
    if (window.matchMedia("(max-width: 767px)").matches) {
      canvasRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [openPanel]);

  // Reset to first slide when slides change
  useEffect(() => { setCurrentIndex(0); }, [slides.length]);

  useEffect(() => {
    onExportReady?.(buildPdfFile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slides,
    carouselDesign,
    themes,
    logoBitmap,
    logoRel,
    currentIndex,
  ]);

  useEffect(() => {
    onPreviewStateChange?.(drawOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselDesign, themes, logoBitmap, logoRel]);

  const updateSlideField = (field, value) => {
    onSlidesChange?.(
      slides.map((s, i) => (i === currentIndex ? { ...s, [field]: value } : s))
    );
  };

  const updateHashtags = (raw) => {
    const list = raw.split(",").map((t) => t.trim()).filter(Boolean);
    updateSlideField("hashtags", list);
  };

  // Builds the carousel as a jsPDF document.
  // Each page is a rasterised slide with an invisible text layer on top,
  // so LinkedIn and screen readers can extract the words.
  const buildPdf = async () => {
    // Lays the slide's own words over the bitmap, rendered invisibly.
    // Costs nothing visually; gives the PDF a real text layer.
    const addTextLayer = (pdf, slide) => {
      const lines = [
        slide?.title,
        slide?.subtitle,
        slide?.body,
        slide?.text,
        Array.isArray(slide?.hashtags) ? slide.hashtags.join(" ") : "",
      ]
        .map((v) => String(v || "").trim())
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .filter((v, i, arr) =>
          !arr.some(
            (o, j) =>
              j !== i && o.length > v.length && o.startsWith(v.replace(/…$/, ""))
          )
        );
      if (!lines.length) return;
      pdf.setFontSize(14);
      pdf.text(lines, CANVAS_W * 0.08, CANVAS_H * 0.12, {
        renderingMode: "invisible",
        maxWidth: CANVAS_W * 0.84,
        lineHeightFactor: 1.4,
      });
    };
    // The slides must be drawn with the real fonts, not a fallback
    await loadCarouselFonts();
    const { jsPDF } = await import("jspdf");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: [CANVAS_W, CANVAS_H],
    });
    const ctx = canvasRef.current.getContext("2d");
    const options = drawOptions();

    for (let i = 0; i < slides.length; i++) {
      drawSlide(ctx, {
        ...options,
        slide: slides[i],
        index: i,
        total: slides.length,
      });
      const dataUrl = canvasRef.current.toDataURL("image/jpeg", 0.95);
      if (i > 0) pdf.addPage([CANVAS_W, CANVAS_H]);
      pdf.addImage(dataUrl, "JPEG", 0, 0, CANVAS_W, CANVAS_H);
      addTextLayer(pdf, slides[i]);
    }

    // restore the slide the user was looking at
    drawSlide(ctx, {
      ...options,
      slide: slides[currentIndex],
      index: currentIndex,
      total: slides.length,
      showLogoHandle: showLogoTools,
    });
    return pdf;
  };

  // Toolbar download — same behaviour as before
  const handleExportPDF = async () => {
    if (!canvasRef.current || !slides.length) return;
    setExporting(true);
    try {
      const pdf = await buildPdf();
      pdf.save("carousel.pdf");
    } catch (err) {
      console.error("Carousel export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  // Upload path — what Home calls at save/publish time
  const buildPdfFile = async (filename = "carousel.pdf") => {
    if (!canvasRef.current || !slides.length) return null;
    const pdf = await buildPdf();
    return new File([pdf.output("blob")], filename, { type: "application/pdf" });
  };

  const slide = slides[currentIndex];
  if (!slides.length) return null;

  return (
    <div id="section-carousel" className="flex-1 flex flex-col">

      {/* Title above the editor, same style as the AI image section */}
      <p className="text-lg sm:text-xl font-bold text-black-800 mb-3 flex gap-2">
        <img src={carouselIcon} alt="" className="w-5 h-6" />
        {t("carousel.previewTitle")}
      </p>

      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-2 p-2 bg-black-20 rounded-tr-lg rounded-tl-lg w-full relative z-20 border border-b-0 border-gray-200">
        {/* One group that wraps where it runs out of space (3 lines on a
            phone, about 2 on desktop). The pressed look shows which tool
            is open. pt-1 leaves room for the NEW badge. */}
        <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-0 w-full pt-1">
          {/* Logo — opens the picker, or toggles the tools bar once a logo is set */}
          <ToolbarTooltipButton
            label={t("logoEditor.uploadLogo")}
            showLabel
            highlight
            badge="New"
            disabled={panelBusy && openPanel !== "logo"}
            variant={openPanel === "logo" ? "iconTextActive" : "iconText"}
            onClick={() => {
              logoBitmap ? togglePanel("logo") : onLogoUploadRequest?.();
            }}
          >
            <img src={logoIcon} alt="logo" className="w-4 h-4" />
          </ToolbarTooltipButton>

          <ToolbarTooltipButton
            label={t("carousel.layout")}
            showLabel
            disabled={panelBusy && openPanel !== "layout"}
            variant={openPanel === "layout" ? "iconTextActive" : "iconText"}
            onClick={() => togglePanel("layout")}
          >
            <img src={layoutIcon} alt="" className="w-4 h-4" />
          </ToolbarTooltipButton>

          <ToolbarTooltipButton
            label={t("carousel.background")}
            showLabel
            disabled={panelBusy && openPanel !== "background"}
            variant={openPanel === "background" ? "iconTextActive" : "iconText"}
            onClick={() => togglePanel("background")}
          >
            <img src={backgroundIcon} alt="" className="w-4 h-4" />
          </ToolbarTooltipButton>

          <ToolbarTooltipButton
            label={t("carousel.fontSettings")}
            showLabel
            disabled={panelBusy && openPanel !== "font"}
            variant={openPanel === "font" ? "iconTextActive" : "iconText"}
            onClick={() => togglePanel("font")}
          >
            <img src={fontSettingsIcon} alt="" className="w-4 h-4" />
          </ToolbarTooltipButton>

          <ToolbarTooltipButton
            label={t("carousel.colors")}
            showLabel
            disabled={panelBusy && openPanel !== "colors"}
            variant={openPanel === "colors" ? "iconTextActive" : "iconText"}
            onClick={() => togglePanel("colors")}
          >
            <img src={colorsIcon} alt="" className="w-4 h-4" />
          </ToolbarTooltipButton>

          <ToolbarTooltipButton
            label={exporting ? t("carousel.exporting") : t("carousel.exportPdf")}
            onClick={handleExportPDF}
            disabled={exporting || panelBusy}
          >
            <span className="text-xs font-bold leading-none">PDF</span>
          </ToolbarTooltipButton>

          <ToolbarTooltipButton
            label={t("insights.preview")}
            onClick={onPreviewClick}
            disabled={panelBusy}
          >
            <img src={eye} alt="preview" className="w-4 h-4" />
          </ToolbarTooltipButton>
        </div>
      </div>

      {/* ── Logo tools — between the toolbar and the canvas ── */}
      {showLogoTools && logoBitmap && logoRel && (
        <div className="bg-black-30 border-x border-gray-200 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-black-600">{t("logoEditor.size")}</span>
                <span className="text-xs font-bold text-black-800">{Math.round(logoRel.wPct * 100)}%</span>
              </div>
              <input
                type="range"
                min={5}
                max={40}
                step={1}
                value={Math.round(logoRel.wPct * 100)}
                onChange={(e) => onLogoChange?.({ ...logoRel, wPct: Number(e.target.value) / 100 })}
                className="w-full accent-primary-500"
              />
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-black-600">{t("logoEditor.opacity")}</span>
                <span className="text-xs font-bold text-black-800">{Math.round((logoRel.opacity ?? 1) * 100)}%</span>
              </div>
              <input
                type="range"
                min={10}
                max={100}
                step={1}
                value={Math.round((logoRel.opacity ?? 1) * 100)}
                onChange={(e) => onLogoChange?.({ ...logoRel, opacity: Number(e.target.value) / 100 })}
                className="w-full accent-primary-500"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={() => { closePanel(); onLogoUploadRequest?.(); }}
              className="text-xs font-bold underline text-black-800 hover:text-black"
            >
              {t("logoEditor.changeLogo")}
            </button>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => { closePanel(); onLogoRemove?.(); }}
                className="!text-sm !py-1.5 !px-4"
              >
                {t("buttons.remove")}
              </Button>
              <Button
                variant="primary"
                onClick={closePanel}
                className="!text-sm !py-1.5 !px-4"
              >
                {t("logoEditor.apply")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Text tools ── */}
      {showFontTools && (
        <CarouselPanel
          onReset={resetFontTools}
          title={t("carousel.fontSettings")}
          onCancel={cancelPanel}
          onApply={closePanel}
        >
          <div className="flex items-center gap-3">
            <label className="text-xs font-bold uppercase tracking-wide text-black-400 whitespace-nowrap">
              {t("carousel.fontFamily")}
            </label>
            <select
              value={design.fontPairId}
              onChange={(e) => updateDesign({ fontPairId: e.target.value })}
              className="flex-1 text-sm p-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              {Object.entries(FONT_PAIRS).map(([id, f]) => (
                <option key={id} value={id}>{f.name}</option>
              ))}
            </select>
          </div>

          {/* Only the sizes used on the slide on screen. A size applies to every slide of that kind. */}
          <div
            className={`grid grid-cols-1 gap-x-4 gap-y-3 ${
              (SIZE_KEYS_BY_SLIDE[slide?.type] || []).length > 1
                ? "sm:grid-cols-2"
                : ""
            }`}
          >
            {(SIZE_KEYS_BY_SLIDE[slide?.type] || []).map((key) => (
              <div key={key} className="flex flex-col gap-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-black-600">
                    {t(`carousel.sizes.${key}`, SIZE_LABELS[key])}
                  </span>
                  <span className="text-xs font-bold text-black-800">
                    {design.sizes[key]}px
                  </span>
                </div>
                <input
                  type="range"
                  min={SIZE_RANGES[key].min}
                  max={SIZE_RANGES[key].max}
                  step={1}
                  value={design.sizes[key]}
                  onChange={(e) => setSize(key, Number(e.target.value))}
                  className="w-full accent-primary-500"
                />
              </div>
            ))}
          </div>
          {slide?.type === "content" && (
            <p className="text-xs text-black-600">
              {t(
                "carousel.sizes.appliesToContent",
                "Applies to all content slides"
              )}
            </p>
          )}
          {/* Layout lines (accent lines, bars, dividers), every slide */}
          <div className="flex flex-col gap-1">
            <div className="flex justify-between items-center">
              <span className="text-xs text-black-600">
                {t("carousel.lineWeight")}
              </span>
              <span className="text-xs font-bold text-black-800">
                {design.lineWeight}
              </span>
            </div>
            <input
              type="range"
              min={LINE_WEIGHT_RANGE.min}
              max={LINE_WEIGHT_RANGE.max}
              step={1}
              value={design.lineWeight}
              onChange={(e) =>
                updateDesign({ lineWeight: Number(e.target.value) })
              }
              className="w-full accent-primary-500"
            />
          </div>

        </CarouselPanel>
      )}

      {/* ── Layout ── */}
      {openPanel === "layout" && (
        <CarouselPanel
          onReset={resetLayout}
          title={t("carousel.layout")}
          onCancel={cancelPanel}
          onApply={closePanel}
        >
          <div className="space-y-2">
            {LAYOUT_IDS.map((id) => {
              const selected = design.layoutId === id;
              // Each card shows the first slide and the first content slide,
              // drawn with this layout and the current text, colors and fonts
              const contentIndex = slides.findIndex((s) => s.type === "content");
              const thumbIndexes = contentIndex > 0 ? [0, contentIndex] : [0];
              return (
                <Button
                  key={id}
                  variant="ghost"
                  aria-pressed={selected}
                  onClick={() => updateDesign({ layoutId: id })}
                  className={`w-full !justify-start text-left !p-2 !gap-3 ${
                    selected ? "bg-primary-200 border-primary-300" : ""
                  }`}
                >
                  <span className="flex gap-1.5 w-32 sm:w-40 shrink-0">
                    {thumbIndexes.map((i) => (
                      <span key={i} className="w-1/2">
                        <CarouselThumb
                          options={{
                            ...drawOptions(),
                            layoutId: id,
                            slide: slides[i],
                            index: i,
                            total: slides.length,
                          }}
                        />
                      </span>
                    ))}
                  </span>
                  <span className="flex flex-col gap-1 min-w-0">
                    <span className="text-base font-bold text-black-800">
                      {t(`carousel.layouts.${id}.name`)}
                    </span>
                    <span className="text-sm font-normal text-black-600 leading-snug">
                      {t(`carousel.layouts.${id}.note`)}
                    </span>
                  </span>
                </Button>
              );
            })}
          </div>
        </CarouselPanel>
      )}

      {/* ── Colors ── */}
      {openPanel === "colors" && (
        <CarouselPanel
          onReset={resetColors}
          title={t("carousel.colors")}
          onCancel={cancelPanel}
          onApply={closePanel}
        >
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(PALETTES).map(([id, p]) => (
              <Button
                key={id}
                variant="ghost"
                onClick={() => updateDesign({ paletteId: id })}
                className={`flex items-center gap-1.5 ${
                  design.paletteId === id ? "bg-primary-200 border-primary-300" : ""
                }`}
              >
                <span className="flex gap-1">
                  <span
                    className="w-3 h-3 rounded-full border border-black-40"
                    style={{ background: p.bg }}
                  />
                  <span
                    className="w-3 h-3 rounded-full border border-black-40"
                    style={{ background: p.text }}
                  />
                  <span
                    className="w-3 h-3 rounded-full border border-black-40"
                    style={{ background: p.accent }}
                  />
                </span>
                <span className="text-black-600">{p.name}</span>
              </Button>
            ))}
            {themes.map((theme) => (
              <Button
                key={theme.theme_id}
                variant="ghost"
                onClick={() => updateDesign({ paletteId: theme.theme_id })}
                className={`flex items-center gap-1.5 ${
                  String(design.paletteId) === String(theme.theme_id)
                    ? "bg-primary-200 border-primary-300"
                    : ""
                }`}
              >
                <span className="flex gap-1">
                  <span
                    className="w-3 h-3 rounded-full border border-black-40"
                    style={{ background: theme.background }}
                  />
                  <span
                    className="w-3 h-3 rounded-full border border-black-40"
                    style={{ background: theme.text }}
                  />
                  <span
                    className="w-3 h-3 rounded-full border border-black-40"
                    style={{ background: theme.highlight }}
                  />
                </span>
                <span className="text-black-600">{theme.name}</span>
              </Button>
             ))}
            <Button
              variant="ghost"
              aria-pressed={isCustom}
              onClick={startCustom}
              className={`!border-dashed ${
                isCustom ? "bg-primary-200 border-primary-300" : ""
              }`}
            >
              <span className="text-black-600">
                + {t("carousel.custom.add")}
              </span>
            </Button>
          </div>

          {isCustom && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {["bg", "text", "accent"].map((key) => (
                  <label
                    key={key}
                    className="flex items-center justify-between gap-2 text-sm text-black-800"
                  >
                    {t(`carousel.custom.${key}`)}
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-black-600 font-mono">
                        {normalizeHex(currentPalette[key])}
                      </span>
                      <input
                        type="color"
                        value={normalizeHex(currentPalette[key]).toLowerCase()}
                        onChange={(e) => setCustomColor(key, e.target.value)}
                        className="w-10 h-8 p-0 border border-black-600 rounded cursor-pointer bg-transparent"
                      />
                    </span>
                  </label>
                ))}
              </div>

              {textContrast < MIN_TEXT_CONTRAST && (
                <p className="text-xs text-black-800 border-l-4 border-warning-500 pl-2">
                  {t("carousel.custom.lowContrast", {
                    ratio: textContrast.toFixed(1),
                  })}
                </p>
              )}

              {themes.length >= themeQuota ? (
                <p className="text-xs text-black-600">
                  {t("carousel.custom.quotaFull", { count: themeQuota })}
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={customName}
                    onChange={(e) => {
                      setCustomName(e.target.value);
                      setCustomSaved(false);
                    }}
                    placeholder={t("userAccountModal.themes.namePlaceholder")}
                    aria-label={t("userAccountModal.themes.name")}
                    maxLength={40}
                    className="flex-1 min-w-[10rem] text-sm p-2 border border-black-40 rounded-lg bg-black-0 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <Button
                    variant="dark"
                    onClick={saveCustomAsBrand}
                    disabled={savingCustom}
                    className="!text-sm !py-1.5 !px-4"
                  >
                    {t("carousel.custom.save")}
                  </Button>
                </div>
              )}
              {customError && (
                <p className="text-xs text-error-500" role="alert">
                  {customError}
                </p>
              )}
            </div>
          )}

          {customSaved && (
            <p className="text-xs text-black-800" role="status">
              {t("carousel.custom.saved")}
            </p>
          )}

          <Button
            variant="link"
            onClick={() => onOpenAccount?.()}
            className="!justify-start"
          >
            {t("sources.brandColors.setupColors")} →
          </Button>
        </CarouselPanel>
      )}

      {/* ── Background ── */}
      {openPanel === "background" && (
        <CarouselPanel
          onReset={resetBackground}
          title={t("carousel.background")}
          onCancel={cancelPanel}
          onApply={closePanel}
        >
          {/* Pattern thumbnails, drawn with the current colors */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {BG_STYLE_IDS.map((id) => {
              const selected = !design.bgBitmap && design.bgStyle === id;
              return (
                <Button
                  key={id}
                  variant="ghost"
                  aria-pressed={selected}
                  onClick={() => updateDesign({ bgStyle: id, bgBitmap: null })}
                  className={`!flex-col !gap-1 !p-1.5 !text-xs ${
                    selected ? "bg-primary-200 border-primary-300" : ""
                  }`}
                >
                  <CarouselThumb
                    backgroundOnly
                    options={{ ...drawOptions(), bgStyle: id, bgBitmap: null }}
                  />
                  <span>{t(`carousel.bgStyles.${id}`)}</span>
                </Button>
              );
            })}
          </div>

          {/* Fine-tuning, folded by default (patterns only) */}
          {!design.bgBitmap && (
            <Button
              variant="link"
              aria-expanded={showBgAdvanced}
              onClick={() => setShowBgAdvanced((v) => !v)}
              className="!justify-start"
            >
              {t("carousel.bg.customize")} {showBgAdvanced ? "▴" : "▾"}
            </Button>
          )}

          {!design.bgBitmap && showBgAdvanced && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3">
                {[
                  ["intensity", 20, 250],
                  ["size", 50, 200],
                  ["density", 25, 200],
                ].map(([key, min, max]) => (
                  <div key={key} className="flex flex-col gap-1">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-black-600">
                        {t(`carousel.bg.${key}`)}
                      </span>
                      <span className="text-xs font-bold text-black-800">
                        {design.bgOptions[key]}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min={min}
                      max={max}
                      step={1}
                      value={design.bgOptions[key]}
                      onChange={(e) =>
                        setBgOption(key, Number(e.target.value))
                      }
                      className="w-full accent-primary-500"
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-black-600">
                {t(`carousel.bgHints.${design.bgStyle}`)}
              </p>

              <div className="flex flex-col gap-1">
                <span className="text-xs text-black-600">
                  {t("carousel.bg.leadingColor")}
                </span>
                <div className="flex flex-wrap gap-2">
                  {["accent", "text"].map((val) => (
                    <Button
                      key={val}
                      variant="ghost"
                      aria-pressed={design.bgOptions.color === val}
                      onClick={() => setBgOption("color", val)}
                      className={`!text-sm !py-1 !px-3 ${
                        design.bgOptions.color === val
                          ? "bg-primary-200 border-primary-300"
                          : ""
                      }`}
                    >
                      {t(`carousel.bg.color_${val}`)}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs text-black-600">
                  {t("carousel.bg.position")}
                </span>
                <div className="flex flex-wrap gap-2">
                  {["none", "h", "v", "hv"].map((val) => (
                    <Button
                      key={val}
                      variant="ghost"
                      aria-pressed={design.bgOptions.mirror === val}
                      onClick={() => setBgOption("mirror", val)}
                      className={`!text-sm !py-1 !px-3 ${
                        design.bgOptions.mirror === val
                          ? "bg-primary-200 border-primary-300"
                          : ""
                      }`}
                    >
                      {t(`carousel.bg.mirror_${val}`)}
                    </Button>
                  ))}
                </div>
              </div>

              {design.bgStyle !== "solid" && (
                <label className="flex items-center gap-2 text-sm text-black-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={design.bgOptions.vary}
                    onChange={(e) => setBgOption("vary", e.target.checked)}
                    className="w-5 h-5 accent-primary-500"
                  />
                  {t("carousel.bg.vary")}
                </label>
              )}

              <Button
                variant="ghost"
                onClick={() => setBgOption("seed", design.bgOptions.seed + 1)}
                className="!text-sm !py-1.5 !px-4"
              >
                <img src={regenerateIcon} alt="" className="w-4 h-4" />
                {t("carousel.bg.shuffle")}
              </Button>
            </div>
          )}

          {/* Own image instead of a pattern */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              variant="ghost"
              onClick={() => bgInputRef.current?.click()}
              className="!text-sm !py-1.5 !px-4"
            >
              <img src={logoIcon} alt="" className="w-4 h-4" />
              {design.bgBitmap
                ? t("carousel.bg.changeImage")
                : t("carousel.bg.uploadImage")}
            </Button>
            {design.bgBitmap && (
              <Button
                variant="link"
                onClick={() => updateDesign({ bgBitmap: null })}
              >
                {t("carousel.bg.removeImage")}
              </Button>
            )}
            <input
              ref={bgInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleBgUpload}
              className="hidden"
            />
          </div>
          {bgError && (
            <p className="text-xs text-error-500" role="alert">
              {bgError}
            </p>
          )}

          {design.bgBitmap && (
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-black-600">
                  {t("carousel.bg.overlay")}
                </span>
                <span className="text-xs font-bold text-black-800">
                  {design.bgOverlay}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={90}
                step={1}
                value={design.bgOverlay}
                onChange={(e) =>
                  updateDesign({ bgOverlay: Number(e.target.value) })
                }
                className="w-full accent-primary-500"
              />
              <p className="text-xs text-black-600">
                {t("carousel.bg.overlayHint")}
              </p>
            </div>
          )}
        </CarouselPanel>
      )}

      {/* ── Canvas ── */}
      <div className="border border-t-0 border-gray-200 rounded-bl-lg rounded-br-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="block mx-auto max-w-full max-h-[70vh] w-auto h-auto"
          style={{
            touchAction: "none",
            cursor: showLogoTools && logoBitmap ? "grab" : "default",
          }}
        />
      </div>

      {/* ── Navigation ── */}
      <div className="flex items-center justify-between mt-3">
        <Button
          variant="ghost"
          onClick={() => setCurrentIndex((i) => clamp(i - 1, 0, slides.length - 1))}
          disabled={currentIndex === 0}
        >
          <img src={arrowLeft} alt="prev" className="w-5 h-5" style={{ filter: "brightness(0)" }} />
        </Button>
        <div className="flex items-center gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`rounded-full transition-all ${
                i === currentIndex
                  ? "w-3 h-3 bg-black-800"
                  : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
              }`}
            />
          ))}
        </div>
        <Button
          variant="ghost"
          onClick={() => setCurrentIndex((i) => clamp(i + 1, 0, slides.length - 1))}
          disabled={currentIndex === slides.length - 1}
        >
          <img src={arrowRight} alt="next" className="w-5 h-5" style={{ filter: "brightness(0)" }} />
        </Button>
      </div>

      {/* ── Per-slide editor ── */}
      {/* Each field has a label with the same name as its size slider */}
      {slide && (
        <div className="mt-4 space-y-3 p-3 bg-black-20 border border-gray-200 rounded-lg">
          <div>
            <h3 className="font-bold text-lg text-black-800">
              {t("carousel.editingSlide")} {currentIndex + 1}
            </h3>
            <p className="text-sm text-black-600">
              {t("carousel.fieldsOptional")}
            </p>
          </div>
          {slide.type === "hook" && (
            <>
            <div>
              <label
                htmlFor="carousel-field-h1"
                className="block text-sm font-bold text-black-800 mb-1"
              >
                {t("carousel.sizes.h1")}
              </label>
              <div className="bg-black-0 rounded-lg border border-gray-200 p-3">
                <textarea
                  id="carousel-field-h1"
                  value={slide.title}
                  onChange={(e) => { updateSlideField("title", e.target.value); autoResize(e.target); }}
                  onInput={(e) => autoResize(e.target)}
                  ref={(el) => autoResize(el)}
                  className="w-full bg-transparent focus:outline-none text-black-800 text-xl leading-normal resize-none px-2 break-words overflow-hidden"
                  style={{ whiteSpace: "pre-wrap", minHeight: "3rem" }}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="carousel-field-subtitle"
                className="block text-sm font-bold text-black-800 mb-1"
              >
                {t("carousel.subtitle")}
              </label>
              <div className="bg-black-0 rounded-lg border border-gray-200 p-3">
                <textarea
                  id="carousel-field-subtitle"
                  value={slide.subtitle || ""}
                  onChange={(e) => { updateSlideField("subtitle", e.target.value); autoResize(e.target); }}
                  onInput={(e) => autoResize(e.target)}
                  ref={(el) => autoResize(el)}
                  rows={1}
                  className="w-full bg-transparent focus:outline-none text-black-800 text-xl leading-normal resize-none px-2 break-words overflow-hidden"
                  style={{ whiteSpace: "pre-wrap", minHeight: "2.5rem" }}
                />
              </div>
            </div>
            </>
          )}
          {slide.type === "content" && (
            <>
            <div>
              <label
                htmlFor="carousel-field-h2"
                className="block text-sm font-bold text-black-800 mb-1"
              >
                {t("carousel.sizes.h2")}
              </label>
              <div className="bg-black-0 rounded-lg border border-gray-200 p-3">
                <textarea
                  id="carousel-field-h2"
                  value={slide.title}
                  onChange={(e) => { updateSlideField("title", e.target.value); autoResize(e.target); }}
                  onInput={(e) => autoResize(e.target)}
                  ref={(el) => autoResize(el)}
                  rows={1}
                  className="w-full bg-transparent focus:outline-none text-black-800 text-xl leading-normal resize-none px-2 break-words overflow-hidden"
                  style={{ whiteSpace: "pre-wrap", minHeight: "2.5rem" }}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="carousel-field-body"
                className="block text-sm font-bold text-black-800 mb-1"
              >
                {t("carousel.sizes.body")}
              </label>
              <div className="bg-black-0 rounded-lg border border-gray-200 p-3">
                <textarea
                  id="carousel-field-body"
                  value={slide.body}
                  onChange={(e) => { updateSlideField("body", e.target.value); autoResize(e.target); }}
                  onInput={(e) => autoResize(e.target)}
                  ref={(el) => autoResize(el)}
                  className="w-full bg-transparent focus:outline-none text-black-800 text-xl leading-normal resize-none px-2 break-words overflow-hidden"
                  style={{ whiteSpace: "pre-wrap", minHeight: "3rem" }}
                />
              </div>
            </div>
            </>
          )}
          {slide.type === "outro" && (
            <>
            <div>
              <label
                htmlFor="carousel-field-cta"
                className="block text-sm font-bold text-black-800 mb-1"
              >
                {t("carousel.sizes.cta")}
              </label>
              <div className="bg-black-0 rounded-lg border border-gray-200 p-3">
                <textarea
                  id="carousel-field-cta"
                  value={slide.title}
                  onChange={(e) => { updateSlideField("title", e.target.value); autoResize(e.target); }}
                  onInput={(e) => autoResize(e.target)}
                  ref={(el) => autoResize(el)}
                  className="w-full bg-transparent focus:outline-none text-black-800 text-xl leading-normal resize-none px-2 break-words overflow-hidden"
                  style={{ whiteSpace: "pre-wrap", minHeight: "3rem" }}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="carousel-field-hashtags"
                className="block text-sm font-bold text-black-800 mb-1"
              >
                {t("carousel.sizes.hashtags")}
              </label>
              <div className="bg-black-0 rounded-lg border border-gray-200 p-3">
                <textarea
                  id="carousel-field-hashtags"
                  value={(slide.hashtags || []).join(", ")}
                  onChange={(e) => { updateHashtags(e.target.value); autoResize(e.target); }}
                  onInput={(e) => autoResize(e.target)}
                  ref={(el) => autoResize(el)}
                  placeholder="hashtag1, hashtag2"
                  rows={1}
                  className="w-full bg-transparent focus:outline-none text-black-800 text-xl leading-normal resize-none px-2 break-words overflow-hidden"
                  style={{ whiteSpace: "pre-wrap", minHeight: "2.5rem" }}
                />
              </div>
            </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}